import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import AppDatePicker from "../shared/AppDatePicker";

const DEFAULT_METHODS = [
    { value: "LINEAR", label: "Linear" },
    { value: "DEGRESSIVE", label: "Degressiv" },
];

const DEFAULT_FORM = {
    name: "",
    acquisitionDate: "",
    acquisitionValue: "",
    usefulLifeYears: "3",
    method: "LINEAR",
    residualValue: "0",
    accountNumber: "",
    invoiceId: null,
    invoiceNumber: null,
    invoiceDate: null,
};

const sanitizeNumber = (value) => {
    if (value === null || value === undefined || value === "") {
        return "";
    }
    return String(value);
};

export default function AssetFormDialog({
    open,
    title,
    initialData,
    onClose,
    onSave,
    saving = false,
    error,
}) {
    const [form, setForm] = useState(DEFAULT_FORM);
    const [validationError, setValidationError] = useState(null);

    useEffect(() => {
        if (!open) {
            setForm(DEFAULT_FORM);
            setValidationError(null);
            return;
        }
        setForm({
            ...DEFAULT_FORM,
            ...initialData,
            acquisitionValue: sanitizeNumber(initialData?.acquisitionValue),
            residualValue: sanitizeNumber(initialData?.residualValue ?? "0"),
            usefulLifeYears: sanitizeNumber(initialData?.usefulLifeYears ?? "3"),
            acquisitionDate: initialData?.acquisitionDate || "",
            accountNumber: initialData?.accountNumber || "",
        });
        setValidationError(null);
    }, [open, initialData]);

    const handleChange = (field) => (event) => {
        const value = event.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const payload = useMemo(() => {
        return {
            ...form,
            acquisitionValue: form.acquisitionValue === "" ? null : Number(form.acquisitionValue),
            residualValue: form.residualValue === "" ? null : Number(form.residualValue),
            usefulLifeYears: form.usefulLifeYears === "" ? null : Number(form.usefulLifeYears),
            accountNumber: form.accountNumber ? form.accountNumber.trim() : null,
        };
    }, [form]);

    const handleSubmit = () => {
        if (!form.name.trim()) {
            setValidationError("Bitte einen Namen angeben.");
            return;
        }
        if (!form.acquisitionDate) {
            setValidationError("Bitte ein Anschaffungsdatum wählen.");
            return;
        }
        if (!payload.acquisitionValue || payload.acquisitionValue <= 0) {
            setValidationError("Der Anschaffungswert muss größer als 0 sein.");
            return;
        }
        if (!payload.usefulLifeYears || payload.usefulLifeYears <= 0) {
            setValidationError("Die Nutzungsdauer muss größer als 0 sein.");
            return;
        }
        if (!form.method.trim()) {
            setValidationError("Bitte eine Abschreibungsmethode wählen.");
            return;
        }
        setValidationError(null);
        onSave(payload);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {validationError && <Alert severity="warning">{validationError}</Alert>}
                    {error && <Alert severity="error">{error}</Alert>}
                    <TextField
                        label="Name"
                        value={form.name}
                        onChange={handleChange("name")}
                        fullWidth
                        autoFocus
                    />
                    <TextField
                        label="Finanzkonto (SKR)"
                        value={form.accountNumber}
                        onChange={handleChange("accountNumber")}
                        fullWidth
                        placeholder="z. B. 0400"
                    />
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <AppDatePicker
                                label="Anschaffungsdatum"
                                value={form.acquisitionDate}
                                onChange={(date) => setForm((prev) => ({ ...prev, acquisitionDate: date }))}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Anschaffungswert"
                                type="number"
                                value={form.acquisitionValue}
                                onChange={handleChange("acquisitionValue")}
                                fullWidth
                                inputMode="decimal"
                                inputProps={{ step: "0.01", min: "0" }}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Nutzungsdauer (Jahre)"
                                type="number"
                                value={form.usefulLifeYears}
                                onChange={handleChange("usefulLifeYears")}
                                fullWidth
                                inputMode="decimal"
                                inputProps={{ min: "1", step: "1" }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Restwert"
                                type="number"
                                value={form.residualValue}
                                onChange={handleChange("residualValue")}
                                fullWidth
                                inputMode="decimal"
                                inputProps={{ step: "0.01", min: "0" }}
                            />
                        </Grid>
                    </Grid>
                    <TextField
                        label="Abschreibungsmethode"
                        select
                        value={form.method}
                        onChange={handleChange("method")}
                        fullWidth
                    >
                        {DEFAULT_METHODS.map((method) => (
                            <MenuItem key={method.value} value={method.value}>
                                {method.label}
                            </MenuItem>
                        ))}
                        {!DEFAULT_METHODS.some((method) => method.value === form.method) && form.method && (
                            <MenuItem value={form.method}>{form.method}</MenuItem>
                        )}
                    </TextField>
                    {(form.invoiceNumber || form.invoiceDate) && (
                        <Stack spacing={0.5} sx={{ mt: 1 }}>
                            <Typography variant="subtitle2">Verknüpfter Beleg</Typography>
                            {form.invoiceNumber && (
                                <Typography variant="body2">Belegnummer: {form.invoiceNumber}</Typography>
                            )}
                            {form.invoiceDate && (
                                <Typography variant="body2">
                                    Belegdatum: {new Date(form.invoiceDate).toLocaleDateString("de-DE")}
                                </Typography>
                            )}
                        </Stack>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Abbrechen
                </Button>
                <Button onClick={handleSubmit} variant="contained" disabled={saving}>
                    {saving ? "Speichern…" : "Speichern"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
